<template>
    <div class="dropbox-container">
        <div
            class="dropbox"
            v-if="!isSuccess && !isFailed"
        >
            <template>
                <input
                    :id="id"
                    type="file"
                    class="form-control"
                    :disabled="isSaving || formSubmitting"
                    name="avatar"
                    @change="filesChange"
                    v-if="isInitial"
                    accept="image/*"
                >

                <div
                    v-if="isInitial"
                    class="dropbox__icon"
                >
                    <svg
                        class="icon d-inline-block"
                        width="26"
                        height="26"
                        aria-hidden="true"
                    >
                        <use xlink:href="#svg-photo" />
                    </svg>
                </div>
                <p
                    v-if="isInitial"
                    v-translate
                >
                    Drag and drop images or browse from files
                </p>
            </template>

            <template>
                <div
                    v-if="isSaving"
                    class="dropbox__icon dropbox__icon--loading"
                >
                    <loader
                        :width="26"
                        :height="26"
                    />
                </div>
                <p
                    v-if="isSaving"
                    v-translate
                >
                    Uploading file
                </p>
            </template>
        </div>

        <div
            class="dropbox__uploaded"
            v-if="isSuccess"
        >
            <div
                class="dropbox__image mr-2"
                :style="`background-image: url('${ uploadedFile.path }')`"
                alt="Uploaded avatar thumbnail"
            ></div>
            <div class="dropbox__uploaded-stats">
                <div
                    class="dropbox__uploaded-stat"
                    v-if="fileName !== ''"
                >
                    {{ fileName }}
                </div>
                <div
                    class="dropbox__uploaded-stat"
                    v-if="fileSize !== ''"
                >
                    {{ fileSize }}
                </div>
                <div class="dropbox__uploaded-controls">
                    <a
                        href="#"
                        @click.prevent="reset()"
                        class="dropbox__edit mr-2"
                        :alt="'Upload a new profile picture' | translate"
                    ></a>
                    <a
                        href="#"
                        class="dropbox__bin"
                        @click.prevent="remove()"
                        :alt="'Remove uploaded profile picture' | translate"
                    ></a>
                </div>
            </div>
        </div>
        <div v-if="isFailed">
            <h2>Uploaded failed.</h2>
            <p>
                <a
                    href="#"
                    @click.prevent="reset()"
                    v-translate
                >Try again</a>
            </p>
            <pre>{{ uploadError }}</pre>
        </div>
        <div v-if="removeFailed">
            <h2>Remove failed.</h2>
            <p>
                <a
                    href="#"
                    @click.prevent="remove()"
                    v-translate
                >Try again</a>
            </p>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { mapMutations, mapState, mapActions } from 'vuex';

const STATUS_INITIAL = 0,
    STATUS_SAVING = 1,
    STATUS_SUCCESS = 2,
    STATUS_FAILED = 3,
    REMOVE_FAILED = 5;

export default {
    name: 'file-upload',
    props: {
        existingFile: {
            type: String,
            default: null
        },
        id: {
            type: String,
            requried: true
        },
        module: {
            type: String,
            required: false,
            default: 'Enrolment'
        }
    },
    watch: {
        /*
		 * If a parent component passes us a file path we want to show that
		 * so we set the state and add it to the included files array
		 */
        existingFile: {
            handler: 'setFile',
            immediate: true // Call setFile on component mount as well
        }
    },
    data: function () {
        return {
            currentStatus: STATUS_INITIAL,
            uploadError: '',
            uploadedFile: {},
            fileName: '',
            fileSize: ''
        };
    },
    computed: {
        ...mapState(['formSubmitting']),
        isInitial() {
            return this.currentStatus === STATUS_INITIAL;
        },
        isSaving() {
            return this.currentStatus === STATUS_SAVING;
        },
        isSuccess() {
            return this.currentStatus === STATUS_SUCCESS;
        },
        isFailed() {
            return this.currentStatus === STATUS_FAILED;
        },
        removeFailed() {
            return this.currentStatus === REMOVE_FAILED;
        }
    },
    methods: {
        ...mapActions('Flash', ['formatAndSetFlash']),
        ...mapMutations({
            setProfilePic: function (commit, payload) {
                return commit(this.module + '/setProfilePic', payload);
            }
        }),
        reset() {
            // reset form to initial state
            this.currentStatus = STATUS_INITIAL;
            this.uploadedFile = {};
            this.uploadError = null;
        },
        remove() {
            if (!this.uploadedFile) return;

            axios
                .post(this.$laraRoute('remove-photo'))
                .then(res => {
                    // Clear the field and reset the upload form
                    this.setProfilePic('');
                    this.reset();
                })
                .catch(err => {
                    this.currentStatus === REMOVE_FAILED;
                    let error = err.response.data.errors || err.message;
                    this.formatAndSetFlash({
                        message: error,
                        key: 'error'
                    });
                });
        },
        save(formData) {
            // upload data to the server
            this.currentStatus = STATUS_SAVING;
            axios
                .post(this.$laraRoute('upload-photo'), formData)
                .then(res => {
                    // Pass the uploaded file to the parent component
                    this.setProfilePic(res.data.path);
                    // Add the file and update the status so we can show it as a thumb
                    this.setFile(res.data);
                })
                .catch(err => {
                    let error = err.response.data.errors || err.message;
                    this.formatAndSetFlash({
                        message: error,
                        key: 'error'
                    });
                    this.reset();
                });
        },
        filesChange(e) {
            let { files, name } = e.target;
            this.fileName = files[0].name;
            this.fileSize = `${(files[0].size / 1000).toFixed(0)}KB`
            // handle file changes
            const formData = new FormData();

            if (!files.length) return;
            if (files.length > 1) return (this.currentStatus = STATUS_FAILED);

            // append the files to FormData
            formData.append(name, files[0], files[0].name);

            // save it
            this.save(formData);
        },
        setFile(upload) {
            let file = upload;

            if (file == null || !file) {
                return;
            }

            if (typeof upload == 'string') {
                this.fileName = this.parseFilename(upload);
                this.$set(this.uploadedFile, 'path', upload);
            } else {
                this.uploadedFile = file;
            }

            this.currentStatus = STATUS_SUCCESS;
        },
        parseFilename(uploadString) {
            let splitStr = uploadString.split('/');
            let targetStr = splitStr[splitStr.length - 1].split('.');
            let name = `${targetStr[0].slice(0, 5)}_`
            return `${name}.${targetStr[1]}`;
        }
    }
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/FileUpload.vue